import { useState, useEffect } from 'react';
import { Play, Square, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { speakFrench, cancelFrench, unlockAudioPlayback } from '../lib/tts';

const GROUPS = [
    { key: 'female', label: 'Female' },
    { key: 'male', label: 'Male' },
    { key: 'other', label: 'Other' },
];

const groupOf = (v) => (v.gender === 'female' || v.gender === 'male' ? v.gender : 'other');

// One voice picker for every plan: Pro and Unlimited differ only in how many
// voices the API returns. The page shows the current voice and a button; the
// list opens on tap, split into Female and Male, with a play button per row.
export default function VoicePicker({ voices, value, onChange, speed, previewText }) {
    const [open, setOpen] = useState(false);
    const [group, setGroup] = useState('female');
    const [playingId, setPlayingId] = useState(null);

    const current = voices.find(v => v.id === value) || voices[0];
    const groups = GROUPS.filter(g => voices.some(v => groupOf(v) === g.key));

    useEffect(() => {
        if (open && current) setGroup(groupOf(current));
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => () => { cancelFrench(); }, []);

    const stop = () => { cancelFrench(); setPlayingId(null); };

    const handleOpenChange = (o) => {
        if (!o) stop();
        setOpen(o);
    };

    const preview = (id) => {
        if (playingId === id) { stop(); return; }
        cancelFrench();
        // Unlock inside the tap: the TTS fetch outlives the gesture, so without
        // this iOS refuses the natural voice.
        unlockAudioPlayback();
        setPlayingId(id);
        // A voice preview is choosing a voice, not practice, so it is not timed.
        speakFrench(previewText, speed, id, null, false, { track: false })
            .catch(() => toast.error('Could not preview this voice'))
            .finally(() => setPlayingId(p => (p === id ? null : p)));
    };

    const choose = (id) => {
        onChange(id);
        stop();
        setOpen(false);
    };

    if (!current) return null;
    const shown = voices.filter(v => groupOf(v) === group);

    return (
        <>
            <div className="flex items-center gap-2 max-w-xs">
                <div
                    data-testid="settings-voice-current"
                    className="flex-1 min-w-0 truncate rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-700"
                >
                    {current.name}
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setOpen(true)}
                    data-testid="settings-voice-change"
                    className="flex-shrink-0"
                >
                    Change voice
                </Button>
            </div>

            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogContent className="max-w-md p-0 gap-0">
                    <DialogHeader className="p-4 pb-3">
                        <DialogTitle>Choose a French voice</DialogTitle>
                        <DialogDescription>Tap play to listen, tap a name to choose it.</DialogDescription>
                    </DialogHeader>

                    {groups.length > 1 && (
                        <div className="px-4 pb-3">
                            <Tabs value={group} onValueChange={(g) => { stop(); setGroup(g); }}>
                                <TabsList className="w-full">
                                    {groups.map(g => (
                                        <TabsTrigger key={g.key} value={g.key} className="flex-1" data-testid={`voice-tab-${g.key}`}>
                                            {g.label} ({voices.filter(v => groupOf(v) === g.key).length})
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                            </Tabs>
                        </div>
                    )}

                    <ul className="max-h-[50vh] overflow-y-auto border-t border-stone-100">
                        {shown.map(v => {
                            const selected = v.id === current.id;
                            return (
                                <li key={v.id} className="flex items-center gap-2 px-4 py-2 border-b border-stone-100 last:border-b-0">
                                    <button
                                        type="button"
                                        onClick={() => preview(v.id)}
                                        aria-label={playingId === v.id ? `Stop ${v.name}` : `Play ${v.name}`}
                                        data-testid={`voice-play-${v.id}`}
                                        className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                                    >
                                        {playingId === v.id ? <Square className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => choose(v.id)}
                                        data-testid={`voice-choose-${v.id}`}
                                        className="flex-1 min-w-0 flex items-center justify-between gap-2 text-left py-2 text-sm text-stone-800"
                                    >
                                        <span className="min-w-0">
                                            <span className="block truncate">{v.name}</span>
                                            {v.description && (
                                                <span className="block text-xs text-stone-500 leading-snug">{v.description}</span>
                                            )}
                                        </span>
                                        {selected && <Check className="h-4 w-4 flex-shrink-0 text-primary" />}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </DialogContent>
            </Dialog>
        </>
    );
}
